import { Component, forwardRef, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { normalizeVisibility } from '../../../../helpers/visibility.util';
import {
  ScribeSuggestionIndicatorComponent,
  ScribeSuggestionIndicatorVariant,
} from '../../shared/scribe-suggestion-indicator/scribe-suggestion-indicator.component';
import { AmendmentFieldIndicatorComponent } from '../../shared/amendment-field-indicator/amendment-field-indicator.component';
import { ScribeSuggestionMarker } from '../../../scribe-suggestion-marker.model';
import {
  clearScribeSuggestionMarker,
  isScribeSuggestionMarked,
} from '../../../scribe-suggestion-marker.util';

export type CheckboxItem = {
  title: string;
  value: boolean;
  visible?: boolean | string | null;
  isScribeSuggested?: ScribeSuggestionMarker;
};

@Component({
  selector: 'fb-emr-checkbox-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ScribeSuggestionIndicatorComponent,
    AmendmentFieldIndicatorComponent,
  ],
  template: `
    <div class="fb-component">
      <div class="fb__header">
        <div class="fb__title">
          <span>{{ title }}</span>
          <fb-scribe-suggestion-indicator
            [target]="getScribeSuggestionMarkerTarget()"
            [variant]="scribeIndicatorVariant"
          ></fb-scribe-suggestion-indicator>
          <fb-amendment-field-indicator [fieldKey]="amendmentCategory" />
        </div>
      </div>
      <div class="fb__checkbox-grid">
        <div
          *ngFor="let item of items; let i = index"
          class="fb__checkbox-item"
          [class.hidden]="!isItemVisible(item)"
        >
          <label class="fb__checkbox-container">
            <input
              type="checkbox"
              [(ngModel)]="item.value"
              [disabled]="_disabled || readonly"
              (change)="onItemChange()"
              class="fb__checkbox"
            />
            <span class="fb__checkbox-label">{{ item.title }}</span>
          </label>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        font-family: Roboto, Arial, sans-serif;
        break-inside: auto;
      }

      /* Component Container */
      .fb-component {
        width: 100%;
        break-inside: avoid;
      }

      /* Header */
      .fb__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 11px;
        page-break-after: avoid;
        break-after: avoid;
        break-inside: avoid;
      }

      .fb__title {
        display: inline-flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 4px 8px;
        font-family: Roboto, Arial, sans-serif;
        font-weight: 500;
        font-size: 14px;
        line-height: 100%;
        color: #000;
        max-width: 100%;
        min-width: 0;
        page-break-after: avoid;
        break-after: avoid;
      }

      .fb__title > :first-child {
        min-width: 0;
        overflow-wrap: anywhere;
      }

      /* Checkbox Grid */
      .fb__checkbox-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
        gap: 10px;
        margin: 0 0;
        break-before: avoid;
      }

      .fb__checkbox-container {
        display: flex;
        align-items: center;
        cursor: pointer;
      }

      .fb__checkbox {
        width: 20px;
        height: 20px;
        border: solid 1px #aaa;
        border-radius: 3px;
        -webkit-appearance: auto !important;
      }

      .fb__checkbox-label {
        margin-left: 8px;
        font-size: 15px;
        font-weight: 400;
        color: #000;
      }

      .hidden {
        display: none;
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FbEmrCheckboxListComponent),
      multi: true,
    },
  ],
})
export class FbEmrCheckboxListComponent implements ControlValueAccessor {
  @Input() title = '';
  /**
   * Name of the medical history category this component edits, for the amendment indicator.
   */
  @Input() amendmentCategory = '';
  @Input() items: CheckboxItem[] = [];
  @Input() readonly = false;
  @Input() scribeIndicatorVariant: ScribeSuggestionIndicatorVariant = 'full';

  _disabled = false;

  // --- ControlValueAccessor
  onChange: (value: CheckboxItem[]) => void = () => void 0;
  onTouched: () => void = () => void 0;

  onItemChange(): void {
    this.items.forEach((item) => clearScribeSuggestionMarker(item));
    this.onChange(this.items);
    this.onTouched();
  }

  writeValue(value: CheckboxItem[] | null): void {
    if (value) {
      this.items = value;
    }
  }

  registerOnChange(fn: (value: CheckboxItem[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
  }

  isItemVisible(item: CheckboxItem): boolean {
    return normalizeVisibility(item?.visible, true);
  }

  getScribeSuggestionMarkerTarget(): CheckboxItem | null {
    return (
      this.items.find((item) => this.isItemVisible(item) && isScribeSuggestionMarked(item)) ?? null
    );
  }
}
